import { useState, useEffect } from 'react';
import { authAPI } from '../services/api';

export interface ActiveTimerData {
  has_checkin: boolean;
  checkin_start: number | null;
  elapsed_seconds: number;
  formatted_time: string;
  running_case_id?: number | null;
  running_case_firm_id?: number | null;
}

export const useActiveTimer = () => {
  const [timerData, setTimerData] = useState<ActiveTimerData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchTimer = async () => {
    try {
      setLoading(true);
      const response = await authAPI.getActiveTimer();
      if (response.data.success) {
        setTimerData(response.data.data);
        setError(null);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch timer');
      setTimerData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTimer();
    
    // Refresh timer every minute
    const interval = setInterval(() => {
      fetchTimer();
    }, 60000); // 60 seconds

    return () => clearInterval(interval);
  }, []);

  // Update formatted time every second if we have check-in data
  useEffect(() => {
    if (!timerData?.has_checkin || !timerData.checkin_start) {
      return;
    }

    const updateTimer = () => {
      const now = Math.floor(Date.now() / 1000);
      const elapsedSeconds = Math.max(0, now - timerData.checkin_start!);
      
      const hours = Math.floor(elapsedSeconds / 3600);
      const minutes = Math.floor((elapsedSeconds % 3600) / 60);
      const seconds = elapsedSeconds % 60;
      const formattedTime = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;

      setTimerData(prev => prev ? {
        ...prev,
        elapsed_seconds: elapsedSeconds,
        formatted_time: formattedTime,
      } : null);
    };

    // Update immediately
    updateTimer();

    // Update every second
    const interval = setInterval(updateTimer, 1000);

    return () => clearInterval(interval);
  }, [timerData?.has_checkin, timerData?.checkin_start]);

  return { timerData, loading, error, refetch: fetchTimer };
};
